import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import type {SetStateAction} from 'react'
import {supportsCodeOrderSort, ViewMode} from './utils/view-mode'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'

/** Internal model for how the sort config is stored in localStorage */
const CommentsSortConfigSchema = Type.Object({
  dateOrder: Type.Enum(['ascending', 'descending'], {description: 'Sort direction when sorting chronologically'}),
  codeOrderOverride: Type.Boolean({
    description:
      'If enabled, will sort by code order in supported view types. Code order sort only shows review comments.',
  }),
})

const CommentsSortConfigValidator = Compile(CommentsSortConfigSchema)

type CommentsSortConfig = Type.Static<typeof CommentsSortConfigSchema>

const defaultCommentsSortConfig = {
  dateOrder: 'ascending',
  codeOrderOverride: true,
} as const satisfies CommentsSortConfig

/** Returns `defaultCommentsSort` if value is not a valid `CommentsSort` object. */
function validOrDefaultSort(unvalidatedSort: unknown) {
  return CommentsSortConfigValidator.Check(unvalidatedSort) ? unvalidatedSort : defaultCommentsSortConfig
}

export const CommentsSort = {
  NewestFirst: 'newest-first',
  OldestFirst: 'oldest-first',
  DiffOrder: 'diff-order',
} as const

export type CommentsSort = (typeof CommentsSort)[keyof typeof CommentsSort]

/** `true` if we are only showing a flat list of review comments, based on the current filter settings. */
export function isReviewCommentsMode(sort: CommentsSort) {
  return sort === CommentsSort.DiffOrder
}

function resolveSortForViewMode(viewMode: ViewMode, config: CommentsSortConfig): CommentsSort {
  if (config.codeOrderOverride && supportsCodeOrderSort(viewMode)) return CommentsSort.DiffOrder

  return config.dateOrder === 'ascending' ? CommentsSort.OldestFirst : CommentsSort.NewestFirst
}

/**
 * Tracks the state of comments sort direction.
 * @param localStorageKey Optional unique ID for local storage keying, so that separate instances can key separately
 * if desired. If not provided, will share the default local storage destination.
 */
export function useCommentsSortState(viewMode: ViewMode, localStorageKey?: string) {
  const [unvalidatedSortConfig, setUnvalidatedSortConfig] = useLocalStorage(
    `pull-request-${localStorageKey ? `${localStorageKey}-` : ''}comments-sort`,
    defaultCommentsSortConfig,
    {validator: CommentsSortConfigValidator},
  )

  const setSort = (action: SetStateAction<CommentsSort>) => {
    setUnvalidatedSortConfig(current => {
      const validCurrentConfig = validOrDefaultSort(current)
      const currentSort = resolveSortForViewMode(viewMode, validCurrentConfig)

      const newSort = typeof action === 'function' ? action(currentSort) : action

      // Diff order always preserves chrono order selection for unsupported views
      if (newSort === CommentsSort.DiffOrder) return {...validCurrentConfig, codeOrderOverride: true}

      return {
        ...validCurrentConfig,
        // Chrono order overrides diff order on supported views, but preserves diff order preference if set from an unsupported view
        ...(supportsCodeOrderSort(viewMode) ? {codeOrderOverride: false} : {}),
        dateOrder: newSort === CommentsSort.NewestFirst ? 'descending' : 'ascending',
      }
    })
  }

  // don't rely on internal useLocalStorage types which are just casting on parse
  const sort = resolveSortForViewMode(viewMode, validOrDefaultSort(unvalidatedSortConfig))

  return [sort, setSort] as const
}
